<template>
  <div class="container">
    <Navigation />
    <div id="container">
      <canvas id="stage" width="300" height="300"></canvas>
    </div>
  </div>
</template>

<script>

import Navigation from '@/components/Navigation'
import SimplexNoise from 'simplex-noise'
import * as PIXI from 'pixi.js'
import * as dat from 'dat.gui'
import * as filters from 'pixi-filters'
import * as filteropts from '@/utils/pixi-filters/index.js'

// helper functions
const R = (m) => Math.round(Math.random()*m)

const simplex = new SimplexNoise()
//simplex.noise3D(x / 16, y / 16, t/16) * 0.5 + 0.5;

//////////////////////////////////

class Demo extends PIXI.Application {
  constructor() {
    const gui = new dat.GUI()

    const domElement = document.querySelector('#container')

    let initWidth = window.innerWidth,
        initHeight = window.innerHeight

    super({
      view: document.querySelector('#stage'),
      width: initWidth,
      height: initHeight,
      autoStart: false,
      backgroundColor: 0x000000,
    })

    PIXI.settings.PRECISION_FRAGMENT = 'highp';

    this.domElement = domElement;

    this.initWidth = initWidth
    this.initHeight = initHeight
    this.animating = true
    this.rendering = true
    this.events = new PIXI.utils.EventEmitter()
    this.animateTimer = 0

    this.bg = null
    this.pond = null
    this.fishCount = 20
    this.fishes = []
    this.fishFilters = []
    this.pondFilters = []

    this.filterArea = new PIXI.Rectangle()
    this.padding = 100
    this.bounds = new PIXI.Rectangle(
        -this.padding,
        -this.padding,
        initWidth + this.padding * 2,
        initHeight + this.padding * 2
    )

    const app = this

    this.gui = gui

    this.gui = gui
    this.gui.add(this, 'rendering')
     .name('&bull; Rendering')
     .onChange((value) => {
         if (!value) {
             app.stop()
         }
         else {
             app.start()
         }
     })

     this.gui.add(this, 'animating')
     .name('&bull; Animating')
  }

  /**
   * Convenience for getting resources
   * @member {object}
   */
  get resources() {
      return this.loader.resources
  }

  /**
   * Load resources
   * @param {object} manifest Collection of resources to load
   */
  load(manifest, callback) {
      this.loader.add(manifest).load(() => {
          callback()
          this.init()
          this.start()
      });
  }

  // called when done loading
  init() {
    const {resources} = this.loader
    const {bounds, initWidth, initHeight} = this

    // Setup the container
    this.pond = new PIXI.Container()
    this.pond.filterArea = this.filterArea
    this.pond.filters = this.pondFilters
    this.stage.addChild(this.pond)

    // Setup the background image
    this.bg = new PIXI.Sprite(resources.background.texture)
    this.pond.addChild(this.bg)

    // Create and add the fish
    for (let i = 0; i < this.fishCount; i++) {
        const id = 'fish' + ((i % 4) + 1)
        const fish = new PIXI.Sprite(resources[id].texture)
        fish.anchor.set(0.5)
        fish.filters = this.fishFilters

        fish.direction = Math.random() * Math.PI * 2
        fish.speed = 2 + Math.random() * 2
        fish.turnSpeed = Math.random() - 0.8

        fish.x = Math.random() * bounds.width
        fish.y = Math.random() * bounds.height

        fish.scale.set(0.8 + Math.random() * 0.3)
        this.pond.addChild(fish)
        this.fishes.push(fish)
    }

    // Setup the tiling sprite
    this.overlay = new PIXI.TilingSprite(
        resources.overlay.texture,
        initWidth,
        initHeight
    );

    // Add the overlay
    this.pond.addChild(this.overlay)

    // Handle window resize event
    window.addEventListener('resize', this.handleResize.bind(this))
    this.handleResize()

    // Handle fish animation
    this.ticker.add(this.animate, this)
  }

  /**
   * Animate the fish, overlay and filters (if applicable)
   * @param {number} delta - % difference in time from last frame render
   */
  animate(delta) {
    this.animateTimer += delta;

    const {bounds, animateTimer, overlay} = this;

    this.events.emit('animate', delta, animateTimer);

    if (!this.animating) {
        return;
    }

    // Animate the overlay
    overlay.tilePosition.x = animateTimer * -1;
    overlay.tilePosition.y = animateTimer * -1;

    for (let i = 0; i < this.fishes.length; i++) {
      const fish = this.fishes[i];

      fish.direction += fish.turnSpeed * 0.01;

      fish.x += Math.sin(fish.direction) * fish.speed;
      fish.y += Math.cos(fish.direction) * fish.speed;

      fish.rotation = -fish.direction - Math.PI/2;

      if (fish.x < bounds.x) {
        fish.x += bounds.width;
      }
      if (fish.x > bounds.x + bounds.width) {
        fish.x -= bounds.width;
      }
      if (fish.y < bounds.y) {
        fish.y += bounds.height;
      }
      if (fish.y > bounds.y + bounds.height) {
        fish.y -= bounds.height;
      }
    }
  }

  /**
   * Resize the demo when the window resizes
   */
  handleResize() {
    const {padding, bg, overlay, filterArea, bounds} = this;

    const width = this.domElement.offsetWidth;
    const height = this.domElement.offsetHeight;
    const filterAreaPadding = 0;

    // Use equivalent of CSS's contain for the background
    // so that it scales proportionally
    const bgAspect = bg.texture.width / bg.texture.height;
    const winAspect = width / height;

    if (winAspect > bgAspect) {
      bg.width = width;
      bg.height = width / bgAspect;
    }
    else {
      bg.height = height;
      bg.width = height * bgAspect;
    }

    bg.x = (width - bg.width) / 2;
    bg.y = (height - bg.height) / 2;

    overlay.width = width;
    overlay.height = height;

    bounds.x = -padding;
    bounds.y = -padding;
    bounds.width = width + padding * 2;
    bounds.height = height + padding * 2;

    filterArea.x = filterAreaPadding;
    filterArea.y = filterAreaPadding;
    filterArea.width = width - filterAreaPadding * 2;
    filterArea.height = height - filterAreaPadding * 2;

    this.events.emit('resize', width, height);

    this.renderer.resize(width, height);

    this.render();
  }

  init_1() {
    const container = new PIXI.Container()
    this.stage.addChild(container)

    container.filters = [new filters.AsciiFilter(20)]

    const texture = PIXI.Texture.from('/img/bugs/circle.png')

    const img = new PIXI.Sprite(texture)
    img.x = 0
    img.y = 0
    img.scale.x *= .5
    img.scale.y *= .5
    container.addChild(img)
  }


  /**
   * Add a new filter
   * @param {string} id Class name
   * @param {object|function} options The class name of filter or options
   * @param {string} [options.id] The name of the PIXI.filters class
   * @param {boolean} [options.global] Filter is in pixi.js
   * @param {array} [options.args] Constructor arguments
   * @param {boolean} [options.fishOnly=false] Apply to fish only, not whole scene
   * @param {boolean} [options.enabled=false] Filter is enabled by default
   * @param {boolean} [options.opened=false] Filter Folder is opened by default
   * @param {function} [oncreate] Function takes filter and gui folder as
   *        arguments and is scoped to the Demo application.
   * @return {PIXI.Filter} Instance of new filter
   */
  addFilter(id, options) {
    if (typeof options === 'function') {
      options = { oncreate: options };
    }

    options = Object.assign({
      name: id,
      enabled: false,
      opened: false,
      args: null,
      fishOnly: false,
      global: false,
      oncreate: null
    }, options);

    if (options.global) {
        options.name += ' (pixi.js)';
    }

    const app = this
    const folder = this.gui.addFolder(options.name)
    const ClassRef = filters[id] || PIXI.filters[id]

    if (!ClassRef) {
        //throw `Unable to find class name with "${id}"`
        console.error(`Unable to find class name with "${id}"`)
        return
    }

    let filter
    if (options.args) {
        const ClassRefArgs = function(a) {
            ClassRef.apply(this, a);
        }

        ClassRefArgs.prototype = ClassRef.prototype
        filter = new ClassRefArgs(options.args)
    }
    else {
        filter = new ClassRef()
    }

    // Set enabled status
    filter.enabled = options.enabled

    folder.add(filter, 'enabled').onChange((enabled) => {
        app.events.emit('enable', enabled);

        this.render()
        if (enabled) {
          folder.domElement.className += ' enabled';
        }
        else {
          folder.domElement.className = folder.domElement.className.replace(' enabled', '')
        }
    });

    if (options.opened) {
      folder.open()
    }

    if (options.enabled) {
      folder.domElement.className += ' enabled';
    }

    if (options.oncreate) {
      options.oncreate.call(filter, folder);
    }

    if (options.fishOnly) {
      this.fishFilters.push(filter);
    }
    else {
      this.pondFilters.push(filter);
    }

    return filter;
  }
}

//&&&&&_______---//&&&&&_______---//&&&&&_______---//&&&&&_______---

export default {
  name: 'Doodles',
  components: {
    Navigation
  },
  created() {
    window.onload = () => {
      let app = new Demo()

      const manifest = [
{ name: 'background', url: '/img/pixi-filters-images/displacement_BG.jpg' },
{ name: 'overlay', url: '/img/pixi-filters-images/overlay.png' },
{ name: 'map', url: '/img/pixi-filters-images/displacement_map.png' },
{ name: 'fish1', url: '/img/pixi-filters-images/displacement_fish1.png' },
{ name: 'fish2', url: '/img/pixi-filters-images/displacement_fish2.png' },
{ name: 'fish3', url: '/img/pixi-filters-images/displacement_fish3.png' },
{ name: 'fish4', url: '/img/pixi-filters-images/displacement_fish4.png' },
{ name: 'lightmap', url: '/img/pixi-filters-images/lightmap.png' },
{ name: 'colormap', url: '/img/pixi-filters-images/colormap.png' }
      ]

      // Load resources then add filters
      app.load(manifest, () => {
        for (const i in filteropts) {
          filteropts[i].call(app)
        }
      })



    }
  }
}

//&&&&&_______---//&&&&&_______---//&&&&&_______---//&&&&&_______---

</script>
<style>
body {
  background: #121212;
}
.dg .title {
  font-size: inherit;
  margin-bottom: inherit;
  color: inherit;
}
.dg .cr.number {
  font-size: inherit;
  border-radius: 0;
  display: block;
  margin-right: 0;
}
.gb .cr .button {
  display: none;
}
</style>
